import React from "react";
import * as Yup from "yup";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import moment from "moment";
import { useParams } from "react-router-dom";
import { Row, Col, Button } from "reactstrap";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import EditorContainer from "../../components/EditorContainer";
import {
  createDevice,
  updateDevice,
  getDevice,
  getHistory,
  unlink
} from "../../api/devices";
import { useAuth0 } from "../../auth/react-auth0-wrapper";
import { getDateTime } from "../../components/TableUtils";
import { it } from "../../utils/content";

const schema = () =>
  Yup.object({
    imei: Yup.string()
      .required("Il codice IMEI è obbligatorio")
      .min(15, "Il codice IMEI deve essere di 15 cifre")
      .max(15, "Il codice IMEI deve essere di 15 cifre"),
    serial: Yup.string().required("Il numero seriale è obbligatorio"),
    model: Yup.string()
      .required("Il modello è obbligatorio")
      .nullable(),
    type: Yup.string()
      .required("Il tipo di dispositivo è obbligatorio")
      .nullable(),
    supplier: Yup.string("Il fornitore è obbligatorio")
      .required("Il fornitore è obbligatorio")
      .nullable(),
    createdat: Yup.string()
      .ensure()
      .required("La data di inserimento è obbligatoria")
      .min(10, "La data deve avere il formato gg/mm/aaaa")
  });

const DeviceEditor = ({ readOnly }) => {
  const { id } = useParams();
  const { domains } = useAuth0();
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const onCreate = async entity => {
    const result = await createDevice({ ...entity });
    return result;
  };

  const onLoad = async id => {
    const device = await getDevice(id);
    const history = await getHistory(id);
    setHistory(history);
    return device;
  };

  const onUpdate = async entity => {
    const result = await updateDevice(entity);
    return result;
  };

  const unlinkDeviceSim = async (serial, iccid) => {
    if (confirm("Sei sicuro di voler continuare?")) {
      try {
        setLoading(true);
        await unlink(serial, iccid);
        window.location.reload();
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <EditorContainer
      key={id ? `${id}-device` : "new-device"}
      canSave={true}
      readOnly={readOnly ? true : false}
      schema={schema}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onLoad={onLoad}
      optionalNav={{ link: "/backoffice/devices", text: "Dispositivi" }}
      redirRoot="/backoffice/devices"
      title={el => `Dispositivo IMEI: ${el.imei || "NUOVO DISPOSITIVO"}`}
      children={(frmProps, containerHandleChange) => {
        const blockIds =
          frmProps.values["status"] && frmProps.values["status"] !== "inserito";
        return (
          <Row>
            <Col sm="6" xs="12">
              <strong className="mt-2">Fornitore</strong>
              <Dropdown
                filter
                disabled={readOnly || blockIds ? true : false}
                filterBy="label"
                className={frmProps.errors.supplier ? "p-error" : ""}
                showClear={true}
                style={{ width: "100%" }}
                value={frmProps.values["supplier"]}
                options={domains.suppliers}
                onChange={e => containerHandleChange("supplier", e)}
              />
              {frmProps.errors.supplier ? (<div className="invalid-feedback">{frmProps.errors.supplier}</div>) : null}
            </Col>
            <Col sm="6" xs="12">
              <strong className="mt-2">Tipo Prodotto</strong>
              <Dropdown
                disabled={readOnly || blockIds ? true : false}
                filter
                filterBy="label"
                id="type"
                className={frmProps.errors.type ? "p-error" : ""}
                value={frmProps.values["type"]}
                showClear={true}
                style={{ width: "100%" }}
                options={domains.devicesType}
                onChange={e => containerHandleChange("type", e)}
              />
              {frmProps.errors.type ? (<div className="invalid-feedback">{frmProps.errors.type}</div>) : null}
            </Col>
            <Col sm="6" xs="12">
              <strong className="mt-2">Seriale</strong>
              <InputText
                disabled={readOnly || blockIds ? true : false}
                className={frmProps.errors.serial ? "p-error" : ""}
                value={frmProps.values["serial"] || ""}
                style={{ width: "100%" }}
                onChange={e => containerHandleChange("serial", e)}
              />
              {frmProps.errors.serial ? (<div className="invalid-feedback">{frmProps.errors.serial}</div>) : null}
            </Col>
            <Col sm="6" xs="12">
              <strong className="mt-2">IMEI</strong>
              <InputText
                disabled={readOnly || blockIds ? true : false}
                maxLength={15}
                className={frmProps.errors.imei ? "p-error" : ""}
                value={frmProps.values["imei"] || ""}
                style={{ width: "100%" }}
                onChange={e => containerHandleChange("imei", e)}
              />
              {frmProps.errors.imei ? (<div className="invalid-feedback">{frmProps.errors.imei}</div>) : null}
            </Col>
            <Col sm="6" xs="12">
              <strong className="mt-2">Modello</strong>
              <InputText
                disabled={readOnly || blockIds ? true : false}
                className={frmProps.errors.model ? "p-error" : ""}
                value={frmProps.values["model"] || ""}
                style={{ width: "100%" }}
                onChange={e => containerHandleChange("model", e)}
              />
              {frmProps.errors.model ? (<div className="invalid-feedback">{frmProps.errors.model}</div>) : null}
            </Col>
            <Col sm="6" xs="12">
              <strong className="mt-2">Riferimento Fattura</strong>
              <InputText
                className={frmProps.errors.rifInvoice ? "p-error" : ""}
                value={frmProps.values["rifInvoice"] || ""}
                style={{ width: "100%" }}
                onChange={e => containerHandleChange("rifInvoice", e)}
              />
            </Col>
            <Col sm="3" xs="12">
              <div><strong className="mt-2">Data Inserimento</strong></div>
              <Calendar
                disabled={readOnly || blockIds ? true : false}
                locale={it}
                monthNavigator={true}
                yearNavigator={true}
                showButtonBar={true}
                maxDate={new Date()}
                className={frmProps.errors.createdat ? "p-error" : ""}
                yearRange="1900:2099"
                value={
                  frmProps.values["createdat"]
                    ? moment(frmProps.values["createdat"]).toDate()
                    : moment().toDate()
                }
                onChange={e => containerHandleChange("createdat", e)}
                dateFormat="dd/mm/yy"
                showIcon={true}
              />
              {frmProps.errors.createdat ? (<div className="invalid-feedback">{frmProps.errors.createdat}</div>) : null}
            </Col>
            <Col sm="3" xs="12">
              <strong className="mt-2">Status</strong>
              <Dropdown
                filter
                filterBy="label"
                id="status"
                className={frmProps.errors.type ? "p-error" : ""}
                value={frmProps.values["status"]}
                showClear={true}
                style={{ width: "100%" }}
                options={domains.devicesStatus}
                onChange={e => containerHandleChange("status", e)}
              />
              {frmProps.errors.type ? (<div className="invalid-feedback">{frmProps.errors.type}</div>) : null}
            </Col>
            {frmProps.values["number"] && (
              <Col sm="6" xs="12">
                <strong className="mt-2">Telefono</strong>
                <InputText
                  disabled={readOnly || blockIds ? true : false}
                  className={frmProps.errors.number ? "p-error" : ""}
                  value={frmProps.values["number"]}
                  style={{ width: "100%" }}
                  onChange={e => containerHandleChange("number", e)}
                />
                {frmProps.errors.number ? (<div className="invalid-feedback">{frmProps.errors.number}</div>) : null}
              </Col>
            )}
            <Col sm="12" xs="12">
              <strong className="mt-2">Note</strong>
              <InputText
                className={frmProps.errors.note ? "p-error" : ""}
                value={frmProps.values["note"] || ""}
                style={{ width: "100%" }}
                onChange={e => containerHandleChange("note", e)}
              />
            </Col>
            {frmProps.values["status"] === "associato" && (
              <Col sm="6" xs="12">
                <strong className="mt-2 mr-2">Elimina associazione</strong>
                <Button
                  disabled={loading ? true : false}
                  className="btn btn-danger  p-button-icon-only"
                  onClick={() =>
                    unlinkDeviceSim(
                      frmProps.values["serial"],
                      frmProps.values["iccid"]
                    )
                  }
                >
                  <i className="pi pi-times"></i>
                </Button>
              </Col>
            )}
          </Row>
        );
      }}
      renderOther={() =>
        history && history.length ? (
          <Row>
            <Col xs="12">
              <DataTable
                key={id ? `${id}-devicehist` : "new-device-hist"}
                value={history}
                paginator={true}
                autoLayout={false}
                rowsPerPageOptions={[5, 10, 20]}
                rows={5}
                className="p-datatable-hoverable-rows p-datatable-customers"
                emptyMessage={() => (<div className="text-center">Nessun Evento in tabella</div>)}
                paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                currentPageReportTemplate="Visualizzati {first} di {last} Eventi su un totale di {totalRecords}"
              >
                <Column field="attributes.operation" header="Eventi" />
                <Column field="createdat" body={getDateTime} header="Effettuato il" />
              </DataTable>
            </Col>
          </Row>
        ) : null
      }
    />
  );
};
export default DeviceEditor;

